import React, { useState } from 'react';
import { Button } from './Button';
import FavoriteButton from './FavoriteButton';
import { ArrowLeftIcon, BookIcon, HeadphonesIcon, ClockIcon, FilterIcon, SearchIcon } from 'lucide-react';
interface FavoritesProps {
  onBack: () => void;
  onItemSelect: (type: string, id: string) => void;
}
interface FavoriteItem {
  id: string;
  title: string;
  description: string;
  type: 'theme' | 'exercise' | 'soundscape';
  duration?: string;
  icon: React.ReactNode;
}
const Favorites: React.FC<FavoritesProps> = ({
  onBack,
  onItemSelect
}) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Sample favorite items
  const favoriteItems: FavoriteItem[] = [{
    id: 'stress',
    title: 'Stress & burn-out',
    description: 'Thema met oefeningen om stress te verminderen',
    type: 'theme',
    icon: <BookIcon className="w-6 h-6" data-id="element-412" />
  }, {
    id: 'stress-1',
    title: 'Ademhalingsoefening',
    description: 'Korte ademhalingsoefening om direct stress te verminderen',
    type: 'exercise',
    duration: '3 min',
    icon: <ClockIcon className="w-6 h-6" data-id="element-413" />
  }, {
    id: 'rain',
    title: 'Zachte Regen',
    description: 'Rustgevende regengeluiden voor focus en ontspanning',
    type: 'soundscape',
    duration: '30 min',
    icon: <HeadphonesIcon className="w-6 h-6" data-id="element-414" />
  }, {
    id: 'focus',
    title: 'Bewustzijn & focus',
    description: 'Strategieën om je concentratie te verbeteren',
    type: 'theme',
    icon: <BookIcon className="w-6 h-6" data-id="element-415" />
  }, {
    id: 'anxiety-2',
    title: 'Angstreductie meditatie',
    description: 'Een geleide meditatie om angstige gedachten los te laten',
    type: 'exercise',
    duration: '10 min',
    icon: <ClockIcon className="w-6 h-6" data-id="element-416" />
  }];
  const filteredItems = favoriteItems.filter(item => {
    // Apply type filter
    if (activeFilter && item.type !== activeFilter) {
      return false;
    }
    // Apply search filter
    if (searchQuery && !item.title.toLowerCase().includes(searchQuery.toLowerCase()) && !item.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  const handleItemClick = (item: FavoriteItem) => {
    onItemSelect(item.type, item.id);
  };
  return <div className="w-full max-w-4xl mx-auto py-4 px-4" data-id="element-417">
      <div className="flex justify-between items-center mb-6" data-id="element-418">
        <div className="flex items-center" data-id="element-419">
          <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100 touch-manipulation" aria-label="Terug" data-id="element-420">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-421" />
          </button>
          <h2 className="text-xl font-light text-gray-800" data-id="element-422">Favorieten</h2>
        </div>
      </div>
      {/* Search and filter */}
      <div className="mb-6" data-id="element-423">
        <div className="relative mb-4" data-id="element-424">
          <input type="text" placeholder="Zoek in favorieten..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400" data-id="element-425" />
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" data-id="element-426" />
        </div>
        <div className="flex space-x-2 overflow-x-auto pb-2" data-id="element-427">
          <button className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border-2 ${activeFilter === null ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveFilter(null)} data-id="element-428">
            Alle
          </button>
          <button className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border-2 ${activeFilter === 'theme' ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveFilter('theme')} data-id="element-429">
            Thema's
          </button>
          <button className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border-2 ${activeFilter === 'exercise' ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveFilter('exercise')} data-id="element-430">
            Oefeningen
          </button>
          <button className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border-2 ${activeFilter === 'soundscape' ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveFilter('soundscape')} data-id="element-431">
            Soundscapes
          </button>
        </div>
      </div>
      {/* Favorites list */}
      <div className="space-y-4" data-id="element-432">
        {filteredItems.length > 0 ? filteredItems.map(item => <div key={`${item.type}-${item.id}`} className="p-5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors duration-200 relative touch-manipulation" onClick={() => handleItemClick(item)} data-id="element-433">
              <div className="absolute top-4 right-4" data-id="element-434">
                <FavoriteButton size="sm" initialState={true} data-id="element-435" />
              </div>
              <div className="flex items-start" data-id="element-436">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-437">
                  {item.icon}
                </div>
                <div data-id="element-438">
                  <h3 className="text-base font-medium text-gray-800 mb-1" data-id="element-439">
                    {item.title}
                  </h3>
                  {item.duration && <p className="text-xs text-gray-500 mb-1" data-id="element-440">
                      {item.duration}
                    </p>}
                  <p className="text-sm text-gray-600" data-id="element-441">{item.description}</p>
                  <div className="mt-2" data-id="element-442">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600" data-id="element-443">
                      {item.type === 'theme' ? 'Thema' : item.type === 'exercise' ? 'Oefening' : 'Soundscape'}
                    </span>
                  </div>
                </div>
              </div>
            </div>) : <div className="p-8 border-2 border-gray-300 rounded-xl text-center" data-id="element-444">
            <p className="text-gray-600 mb-4" data-id="element-445">
              {searchQuery ? `Geen favorieten gevonden voor "${searchQuery}"` : 'Geen favorieten gevonden met de geselecteerde filter'}
            </p>
            <Button variant="secondary" onClick={() => {
          setSearchQuery('');
          setActiveFilter(null);
        }} data-id="element-446">
              Alle favorieten tonen
            </Button>
          </div>}
      </div>
    </div>;
};
export default Favorites;